import { useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useState } from 'react';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

interface SSOConfig {
  configured: boolean;
  entityId: string | null;
  ssoUrl: string | null;
  metadataUrl: string | null;
  enabledAt: string | null;
  certificateFingerprint: string | null;
}

export function SettingsSSORoute() {
  const getToken = useSessionTokenGetter();
  const [config, setConfig] = useState<SSOConfig | null>(null);
  const [upgradeRequired, setUpgradeRequired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    metadataUrl: '',
    entityId: '',
    ssoUrl: '',
    certificate: '',
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/settings/sso`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 402) {
        if (!cancelled) setUpgradeRequired(true);
      } else if (res.ok) {
        const data = (await res.json()) as SSOConfig;
        if (!cancelled) {
          setConfig(data);
          setForm({
            metadataUrl: data.metadataUrl ?? '',
            entityId: data.entityId ?? '',
            ssoUrl: data.ssoUrl ?? '',
            certificate: '',
          });
        }
      }
      if (!cancelled) setLoading(false);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  const handleSave = async () => {
    setSaving(true);
    const token = await getToken();
    const payload = form.metadataUrl
      ? { metadataUrl: form.metadataUrl }
      : { entityId: form.entityId, ssoUrl: form.ssoUrl, certificate: form.certificate };

    const res = await fetch(`${API}/api/v1/settings/sso`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      const token2 = await getToken();
      const refreshed = await fetch(`${API}/api/v1/settings/sso`, {
        headers: { Authorization: `Bearer ${token2}` },
      });
      if (refreshed.ok) setConfig((await refreshed.json()) as SSOConfig);
    }
    setSaving(false);
  };

  if (loading) return <div className="px-6 py-10 text-sm text-slate-500">Loading…</div>;

  if (upgradeRequired) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-10">
        <div className="rounded-lg border border-purple-200 bg-purple-50 p-6 text-center">
          <p className="text-sm font-semibold text-purple-900">
            SAML SSO requires an Enterprise plan
          </p>
          <p className="mt-2 text-xs text-purple-700">
            Contact sales to upgrade and unlock single sign-on.
          </p>
          <a
            href="mailto:sales@develio.com"
            className="mt-4 inline-block rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
          >
            Contact sales
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-10">
      <h1 className="mb-6 text-xl font-semibold text-slate-900">SAML SSO</h1>

      {config?.configured && (
        <div className="mb-4 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          SAML configured. Enabled{' '}
          {config.enabledAt ? new Date(config.enabledAt).toLocaleDateString() : ''}.
          {config.certificateFingerprint && (
            <span className="ml-2 font-mono text-xs">{config.certificateFingerprint}</span>
          )}
        </div>
      )}

      <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-6">
        <div>
          <label
            htmlFor="sso-metadata-url"
            className="mb-1 block text-xs font-medium text-slate-600"
          >
            Metadata URL (recommended)
          </label>
          <input
            id="sso-metadata-url"
            type="url"
            placeholder="https://your-idp.com/saml/metadata"
            value={form.metadataUrl}
            onChange={(e) => setForm({ ...form, metadataUrl: e.target.value })}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
          />
          <p className="mt-1 text-xs text-slate-400">
            If your IdP provides a metadata URL, enter it above and skip the manual fields below.
          </p>
        </div>

        <div className="relative flex items-center gap-3">
          <div className="flex-1 border-t border-slate-200" />
          <span className="text-xs text-slate-400">or configure manually</span>
          <div className="flex-1 border-t border-slate-200" />
        </div>

        <div>
          <label htmlFor="sso-entity-id" className="mb-1 block text-xs font-medium text-slate-600">
            Entity ID (Issuer)
          </label>
          <input
            id="sso-entity-id"
            type="text"
            placeholder="https://your-idp.com/saml"
            value={form.entityId}
            onChange={(e) => setForm({ ...form, entityId: e.target.value })}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
          />
        </div>

        <div>
          <label htmlFor="sso-url" className="mb-1 block text-xs font-medium text-slate-600">
            SSO URL
          </label>
          <input
            id="sso-url"
            type="url"
            placeholder="https://your-idp.com/saml/sso"
            value={form.ssoUrl}
            onChange={(e) => setForm({ ...form, ssoUrl: e.target.value })}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
          />
        </div>

        <div>
          <label
            htmlFor="sso-certificate"
            className="mb-1 block text-xs font-medium text-slate-600"
          >
            x.509 Certificate (PEM)
          </label>
          <textarea
            id="sso-certificate"
            rows={4}
            placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
            value={form.certificate}
            onChange={(e) => setForm({ ...form, certificate: e.target.value })}
            className="w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
          />
        </div>

        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving}
          className="w-full rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save SAML Configuration'}
        </button>
      </div>
    </div>
  );
}
